import React, { useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import Button from '../Button';
import { AIMagicStarIcon } from '../Icons';
import { generateAISummary } from '../../../graphql/likiees/mutations';
import { useCredits } from '../../../hooks/useCredits';
import './AISummaryButton.scss';

const AISummaryButton = ({
  likieeId,
  onSuccess,
  disabled = false,
  className = '',
  isRegenerate = false,
  showCreditCost = true, // Default to true to show credit cost
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const history = useHistory();
  const { checkCredits } = useCredits();
  const [generating, setGenerating] = useState(false);

  // Get credit cost based on operation type
  const creditCost = isRegenerate ? 1 : 3; // Regenerate costs 1 credit, Generate costs 3 credits
  const buttonText = isRegenerate ? 'Regenerate' : 'Generate AI Summary';

  const handleGenerateAISummary = async () => {
    if (disabled || generating) return;

    // Check if user has sufficient credits
    const hasEnoughCredits = checkCredits('AI_ENHANCEMENT', creditCost);

    if (!hasEnoughCredits) {
      // Redirect to subscription page if insufficient credits
      history.push('/profile/subscription');
      return;
    }

    setGenerating(true);
    try {
      // Call the generateAISummary mutation directly
      const result = await API.graphql(
        graphqlOperation(generateAISummary, {
          likieeId: likieeId,
        }),
      );

      // Check if the mutation was successful
      if (result.data?.generateAISummary?.error) {
        throw new Error(result.data.generateAISummary.error);
      }

      const successMessage = isRegenerate
        ? t('ai_summary_regeneration_started', 'AI summary regeneration started')
        : t('ai_summary_generation_started', 'AI summary generation started');

      alert.success(successMessage);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = isRegenerate
        ? t('ai_summary_regeneration_failed', 'AI summary regeneration failed')
        : t('ai_summary_generation_failed', 'AI summary generation failed');

      alert.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button
      className={`ai-summary-button ${className} ${isRegenerate ? 'regenerate' : 'generate'} ${
        generating ? 'loading' : ''
      }`}
      onClick={handleGenerateAISummary}
      disabled={disabled || generating}
      loading={generating}
      variant='outline'
      size='md'
    >
      <span className='button-content'>
        <AIMagicStarIcon className='ai-icon' />
        <span className='button-text'>
          {generating
            ? isRegenerate
              ? t('regenerating...', 'Regenerating...')
              : t('generating...', 'Generating...')
            : t(buttonText)}
        </span>
        {showCreditCost && (
          <span className='credit-cost'>
            {creditCost} credit{creditCost !== 1 ? 's' : ''}
          </span>
        )}
      </span>
    </Button>
  );
};
export default AISummaryButton;
